/**
 * Rich-text editor for campaign body copy.
 *
 * Wraps `@uiw/react-md-editor`, already a Gluon dependency and already used by
 * the evals pages — so this is the house editor rather than a new one, and it
 * brings a real toolbar, live side-by-side preview, keyboard shortcuts, undo,
 * and selection-aware formatting for free.
 *
 * Markdown rather than HTML because that is what the app can render:
 * `react-native-markdown-display` ships in the binary and is used in production
 * by AI Dispatch. It also keeps the stored payload plain text, so the length cap
 * and link validation in the shared schema still apply to it unchanged.
 *
 * The toolbar is CURATED to the constructs the mobile renderer actually styles
 * (`PromoDetailScreen`'s markdown styles): bold, italic, headings, lists, links,
 * quote. Tables, images, and code are deliberately absent — offering them would
 * imply support the detail screen does not have.
 */

import { useMemo } from 'react'
import Box from '@mui/joy/Box'
import Stack from '@mui/joy/Stack'
import Typography from '@mui/joy/Typography'
import MDEditor, { commands } from '@uiw/react-md-editor'

interface Props {
    value: string
    onChange: (next: string) => void
    maxLength: number
    height?: number
}

/**
 * Tags the preview is allowed to render.
 *
 * This is the actual XSS control, and it has to be an allow-list rather than a
 * flag: `@uiw/react-markdown-preview` injects `rehypeRaw` unconditionally
 * (`esm/index.js`), so any raw HTML in the body is already a real element node
 * by the time react-markdown's own `skipHtml` is consulted — that check only
 * ever matches `node.type === 'raw'`, so it strips nothing here. (The prop is
 * also forwarded inverted, `skipHtml={!skipHtml}`, which makes passing it
 * doubly moot.)
 *
 * That matters because this PR's governance flow is deliberately two-person:
 * operator A writes a campaign body and operator B opens it to APPROVE, so a
 * payload runs in B's session — and B's session holds the bearer token that
 * `requireGluonOperator` gates.
 *
 * The list is exactly what the toolbar can produce and `PromoDetailScreen`
 * styles. `<iframe>`, `<script>`, and `<img>` are absent by construction rather
 * than by blocklist, so a tag nobody thought of is denied by default.
 */
const ALLOWED_TAGS = new Set(['p', 'br', 'strong', 'em', 'del', 's', 'a', 'ul', 'ol', 'li', 'blockquote', 'h1', 'h2', 'h3', 'code', 'pre', 'hr'])

/**
 * Allow only safe url schemes.
 *
 * Needed IN ADDITION to the tag list: `react-markdown` normally strips
 * `javascript:` hrefs itself, but the preview component overrides
 * `urlTransform` with the identity function (`url => url`), which disables that
 * protection — so `[click](javascript:…)` survives the tag allow-list on an
 * otherwise-legitimate `<a>`.
 *
 * Control characters are stripped before testing because `java\tscript:` and
 * friends are parsed as a scheme by browsers but would slip a naive prefix test.
 */
function safePromoUrl(url: string): string {
    // eslint-disable-next-line no-control-regex
    const cleaned = String(url).replace(/[ - ]/g, '').toLowerCase()
    return /^(https?:|mailto:|#|\/)/.test(cleaned) || cleaned === '' ? url : ''
}

/** Only what `PromoDetailScreen` styles — see the file header. */
const TOOLBAR = [
    commands.bold,
    commands.italic,
    commands.strikethrough,
    commands.divider,
    commands.title2,
    commands.title3,
    commands.divider,
    commands.unorderedListCommand,
    commands.orderedListCommand,
    commands.quote,
    commands.divider,
    commands.link,
]

export function MarkdownField({ value, onChange, maxLength, height = 260 }: Props) {
    const remaining = maxLength - value.length
    const nearLimit = remaining <= maxLength * 0.1

    // Enforce the cap on input rather than letting a paste blow past it and fail
    // at save with a schema error the operator has to decode.
    const handleChange = useMemo(
        () => (next?: string) => {
            const text = next ?? ''
            onChange(text.length > maxLength ? text.slice(0, maxLength) : text)
        },
        [onChange, maxLength]
    )

    return (
        <Stack spacing={0.5}>
            {/* `data-color-mode` is how react-md-editor picks its palette; pinned to
                light so the editor matches Gluon's surface regardless of OS theme. */}
            <Box
                data-color-mode="light"
                sx={{
                    '& .w-md-editor': { borderRadius: 8, border: '1px solid', borderColor: 'neutral.outlinedBorder', boxShadow: 'none' },
                    '& .w-md-editor-toolbar': { borderRadius: '8px 8px 0 0', padding: '6px 8px' },
                    '& .w-md-editor-content': { borderRadius: '0 0 8px 8px' },
                    // The preview pane renders the same subset the app does.
                    '& .wmde-markdown': { fontSize: 14, lineHeight: 1.55 },
                }}
            >
                <MDEditor
                    value={value}
                    onChange={handleChange}
                    commands={TOOLBAR}
                    // No fullscreen/edit-mode toggles — they add chrome without
                    // helping for a short marketing blurb.
                    extraCommands={[]}
                    preview="live"
                    height={height}
                    // Sanitization — see ALLOWED_TAGS / safePromoUrl above. Both halves
                    // are required: the tag list alone still lets a `javascript:` href
                    // through on a legitimate <a>.
                    //
                    // This also keeps the preview HONEST, which is the reason to strip
                    // rather than escape: the device never renders HTML either. Mobile
                    // builds `MarkdownIt({ typographer, breaks, linkify })` with no
                    // `html: true`, and markdown-it defaults that to false — so HTML
                    // reaches the phone as literal text. Dropping it here means the
                    // preview and the app agree, instead of the preview being the only
                    // place the markup appears to work.
                    previewOptions={{
                        allowElement: (element) => ALLOWED_TAGS.has(element.tagName),
                        urlTransform: safePromoUrl,
                    }}
                    textareaProps={{ placeholder: 'Describe the offer. Supports bold, italic, headings, lists, and links.' }}
                />
            </Box>
            <Typography level="body-xs" color={nearLimit ? 'warning' : 'neutral'}>
                {value.length} / {maxLength}
            </Typography>
        </Stack>
    )
}
